import { ErrorCode, type PublishValidationError } from '@bliks-io/contracts';

const ERROR_CODES = new Set<string>(Object.values(ErrorCode));

function matchesStatus(error: unknown, status: number): boolean {
  if (typeof error !== 'object' || error === null) return false;
  return (error as { statusCode?: number }).statusCode === status;
}

export function isNotFoundError(error: unknown): boolean {
  return matchesStatus(error, 404);
}

/**
 * A missing entity is the common failure, but a 500/403/offline must not claim
 * the entity is gone — the breadcrumb would disagree with the page.
 */
export function notFoundOrUnknownKey(error: unknown, notFoundKey: string): string {
  return !error || isNotFoundError(error) ? notFoundKey : 'errors.unknown';
}

export function isConflictError(error: unknown): boolean {
  return matchesStatus(error, 409);
}

export function isForbiddenError(error: unknown): boolean {
  return matchesStatus(error, 403);
}

export function isPublishValidationError(error: unknown): error is Partial<PublishValidationError> {
  if (!matchesStatus(error, 400)) return false;
  const { contentErrors, graphErrors } = error as Partial<PublishValidationError>;
  return contentErrors != null || graphErrors != null;
}

/**
 * The i18n error code the backend's I18n*Exception carries in the response
 * body's `message` field (e.g. "errors.totp.invalidCode"), or null when the
 * error carries no known code. Reads the enriched ApiError the agent's
 * response interceptor rejects with — the raw axios error never reaches a
 * caller.
 */
export function getApiErrorCode(error: unknown): ErrorCode | null {
  if (typeof error !== 'object' || error === null) return null;
  const { message } = error as { message?: unknown };
  return typeof message === 'string' && ERROR_CODES.has(message) ? (message as ErrorCode) : null;
}

/**
 * True when the backend rejected a sensitive operation because the session's
 * authentication is too old (SensitiveOperationGuard, 403) — the caller
 * should offer step-up re-authentication instead of a generic error toast.
 */
export function isReauthRequiredError(error: unknown): boolean {
  return getApiErrorCode(error) === ErrorCode.AUTH_REAUTH_REQUIRED;
}
